import { useEffect } from 'react';
import {
  readActiveChatFromSessionStorage,
  readDisplayedFromBrowserStorage,
} from '@/aiAssistant/aiAssistantStorage.utilities';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';
import type { ExternalAgentBuilderPrompt } from '@/agentBuilder/agentBuilder.types';
import type {
  AIMemory,
  AgentType,
  AskUserAnswer,
  AskUserDraft,
  AskUserQuestion,
  ChatAttachment,
  ChatMessage,
  ExternalAIAssistantPayload,
  FontSize,
  ReasoningEffort,
  ReasoningStyle,
  SelectedDocument,
} from '@/aiAssistant/aiAssistant.types';

export interface AiAssistantState {
  /** Whether the AI Assistant panel is open. Not persisted to the backend; see {@link aiAssistantStorage.utilities}. */
  displayed: boolean;
  /** Whether the AI Assistant panel is collapsed to its minimized affordance. */
  minimized: boolean;
  /** Whether the AI Assistant panel is expanded to its maximized size. */
  maximized: boolean;
  /** Whether the prompt library is displayed. */
  libraryDisplayed: boolean;
  /** Whether the prompt library editor is displayed. */
  libraryEditorDisplayed: boolean;
  /** Whether the next time the panel opens it should start a new chat rather than resume the active one. */
  startNewChatOnOpen: boolean;
  /** The messages in the currently active chat. */
  messages: ChatMessage[];
  /** The agent currently selected in the AI Assistant. `'vantage'`/`'workbench'` are legacy agents that are no
   * longer selectable but may still be set on existing chats. */
  selectedAgent: AgentType | 'vantage' | 'workbench' | undefined;
  /** The font size used to render chat messages. */
  fontSize: FontSize;
  /** A prompt submitted from outside the AI Assistant chat panel (e.g. ActionBoards, HelpCenter) awaiting handling. */
  externalPrompt: ExternalAIAssistantPayload | undefined;
  /** An agent builder prompt submitted from outside the AI Assistant chat panel awaiting handling. */
  externalAgentBuilderPrompt: ExternalAgentBuilderPrompt | undefined;
  /** Whether reasoning-lane agents show their reasoning process. */
  reasoningStyle: ReasoningStyle;
  // The reasoning effort used by reasoning-lane agents. Session override; undefined uses the selected agent's default.
  reasoningEffort: ReasoningEffort | undefined;
  refreshHistory: boolean;
  /** The chat currently active in the panel. Not persisted to the backend; tracked per-tab in sessionStorage. */
  activeChat: { id: string; name: string } | undefined;
  /** A chat requested from outside the panel (e.g. the Agent Builder Runs table), consumed once by the organism. */
  chatToOpen: { id: string; name: string } | undefined;
  /** The current search value in the chat history list. */
  historySearchValue: string;
  /** The document(s) currently referenced as context for the next prompt. */
  selectedDocument: SelectedDocument | SelectedDocument[] | null;
  /** Whether memories should be generated from the conversation. */
  generateMemory: boolean;
  savedMemories: AIMemory[];
  /** The subsection of the AI preferences tab to focus, mainly used to redirect into the memories UI. */
  preferredSubsection: 'general' | 'memories' | 'privacy' | null;
  /** Attachments staged for the next message but not yet sent. */
  draftAttachments: ChatAttachment[];
  /** The library item currently open in the library editor, or `null` if none. */
  editingLibraryItemId: string | null;
  /** Pending/answered/skipped ask_user (clarifying question) prompts, keyed by question id. */
  askUserQuestions: Record<string, AskUserQuestion>;
  /** In-progress drafts (selected letter or free-form text) for open ask_user questions, keyed by question id. */
  askUserDrafts: Record<string, AskUserDraft>;
  /** Whether the docked Questions panel is collapsed. */
  askUserPanelCollapsed: boolean;
}

export type AiAssistantDehydratedState = Pick<
  AiAssistantState,
  | 'maximized'
  | 'libraryDisplayed'
  | 'libraryEditorDisplayed'
  | 'selectedAgent'
  | 'fontSize'
  | 'reasoningStyle'
  | 'refreshHistory'
  | 'generateMemory'
  | 'savedMemories'
  | 'editingLibraryItemId'
  | 'askUserQuestions'
  | 'askUserDrafts'
  | 'askUserPanelCollapsed'
>;

export interface AiAssistantActions {
  setDisplayed: (displayed: boolean) => void;
  setMinimized: (minimized: boolean) => void;
  setMaximized: (maximized: boolean) => void;
  setLibraryDisplayed: (libraryDisplayed: boolean) => void;
  setStartNewChatOnOpen: (startNewChatOnOpen: boolean) => void;
  setMessages: (messages: ChatMessage[]) => void;
  setSelectedAgent: (agent: AgentType) => void;
  setFontSize: (fontSize: FontSize) => void;
  setExternalPrompt: (payload: ExternalAIAssistantPayload | undefined) => void;
  setExternalAgentBuilderPrompt: (payload: ExternalAgentBuilderPrompt | undefined) => void;
  setReasoningStyle: (reasoningStyle: ReasoningStyle) => void;
  setReasoningEffort: (reasoningEffort: ReasoningEffort | undefined) => void;
  setRefreshHistory: (refreshHistory: boolean) => void;
  setActiveChat: (chat: { id: string; name: string } | undefined) => void;
  setChatToOpen: (chat: { id: string; name: string } | undefined) => void;
  setHistorySearchValue: (searchValue: string) => void;
  setSelectedDocument: (document: SelectedDocument | SelectedDocument[] | null) => void;
  setGenerateMemory: (generateMemory: boolean) => void;
  setSavedMemories: (savedMemories: AIMemory[]) => void;
  setPreferredSubsection: (subsection: 'general' | 'memories' | 'privacy' | null) => void;
  addMemory: (memory: AIMemory) => void;
  removeMemory: (memoryId: string) => void;
  updateMemory: (memory: AIMemory) => void;
  addDraftAttachment: (attachment: ChatAttachment) => void;
  removeDraftAttachment: (attachmentId: string) => void;
  clearDraftAttachments: () => void;
  setLibraryEditorState: (id: string | null, libraryEditorDisplayed: boolean) => void;
  /** Adds a pending ask_user question to the store. No-ops if the question id is already present. */
  addAskUserQuestion: (question: AskUserQuestion) => void;
  /** Updates (or, with `replace`, fully replaces) the draft for a still-open ask_user question. */
  updateAskUserDraft: (questionId: string, draft: AskUserDraft, replace?: boolean) => void;
  /** Marks a question as answered/skipped, replaces its draft with the final answer, and clears its in-progress draft. */
  resolveAskUserQuestion: (questionId: string, answer: AskUserAnswer) => void;
  /** Removes all pending ask_user questions (and their drafts) for the given chat, e.g. on stream abort. */
  cancelAskUserQuestionsForChat: (chatId: string) => void;
  setAskUserPanelCollapsed: (collapsed: boolean) => void;
  reset: (overrides?: Partial<AiAssistantState>) => void;
}

const initialState: AiAssistantState = Object.freeze({
  displayed: readDisplayedFromBrowserStorage() ?? false,
  minimized: false,
  maximized: false,
  libraryDisplayed: false,
  libraryEditorDisplayed: false,
  startNewChatOnOpen: false,
  messages: [],
  selectedAgent: undefined,
  fontSize: 'small',
  externalPrompt: undefined,
  externalAgentBuilderPrompt: undefined,
  reasoningStyle: 'enabled',
  // Session override; undefined uses the selected agent's default (see getDefaultReasoningEffort). Not persisted —
  // persisting an explicit override would incorrectly carry it over to a different agent in a later session.
  reasoningEffort: undefined,
  refreshHistory: true,
  activeChat: readActiveChatFromSessionStorage(),
  chatToOpen: undefined,
  historySearchValue: '',
  selectedDocument: null,
  generateMemory: true,
  savedMemories: [],
  preferredSubsection: null,
  draftAttachments: [],
  editingLibraryItemId: null,
  askUserQuestions: {},
  askUserDrafts: {},
  askUserPanelCollapsed: false,
});

export const dehydrateAiAssistantState = (state: AiAssistantState): AiAssistantDehydratedState => ({
  maximized: state.maximized,
  libraryDisplayed: state.libraryDisplayed,
  libraryEditorDisplayed: state.libraryEditorDisplayed,
  selectedAgent: state.selectedAgent,
  fontSize: state.fontSize,
  reasoningStyle: state.reasoningStyle,
  refreshHistory: state.refreshHistory,
  generateMemory: state.generateMemory,
  savedMemories: state.savedMemories,
  editingLibraryItemId: state.editingLibraryItemId,
  askUserQuestions: state.askUserQuestions,
  askUserDrafts: state.askUserDrafts,
  askUserPanelCollapsed: state.askUserPanelCollapsed,
});

const aiAssistantActionsFactory: ActionsFactory<AiAssistantState, AiAssistantActions> = (set, _get, resetState) => ({
  setDisplayed: (displayed) =>
    set((draft) => {
      draft.displayed = displayed;
    }),

  setMinimized: (minimized) =>
    set((draft) => {
      draft.minimized = minimized;
    }),

  setMaximized: (maximized) =>
    set((draft) => {
      draft.maximized = maximized;
    }),

  setLibraryDisplayed: (libraryDisplayed) =>
    set((draft) => {
      draft.libraryDisplayed = libraryDisplayed;
    }),

  setStartNewChatOnOpen: (startNewChatOnOpen) =>
    set((draft) => {
      draft.startNewChatOnOpen = startNewChatOnOpen;
    }),

  setMessages: (messages) =>
    set((draft) => {
      draft.messages = messages;
    }),

  setSelectedAgent: (agent) =>
    set((draft) => {
      draft.selectedAgent = agent;
    }),

  setFontSize: (fontSize) =>
    set((draft) => {
      draft.fontSize = fontSize;
    }),

  setExternalPrompt: (payload) =>
    set((draft) => {
      draft.externalPrompt = payload;
    }),

  setExternalAgentBuilderPrompt: (payload) =>
    set((draft) => {
      draft.externalAgentBuilderPrompt = payload;
    }),

  setReasoningStyle: (reasoningStyle) =>
    set((draft) => {
      draft.reasoningStyle = reasoningStyle;
    }),

  setReasoningEffort: (reasoningEffort) =>
    set((draft) => {
      draft.reasoningEffort = reasoningEffort;
    }),

  setRefreshHistory: (refreshHistory) =>
    set((draft) => {
      draft.refreshHistory = refreshHistory;
    }),

  setActiveChat: (chat) =>
    set((draft) => {
      draft.activeChat = chat;
    }),

  setChatToOpen: (chat) =>
    set((draft) => {
      draft.chatToOpen = chat;
    }),

  setHistorySearchValue: (searchValue) =>
    set((draft) => {
      draft.historySearchValue = searchValue;
    }),

  setSelectedDocument: (document) =>
    set((draft) => {
      draft.selectedDocument = document;
    }),

  setGenerateMemory: (generateMemory) =>
    set((draft) => {
      draft.generateMemory = generateMemory;
    }),

  setSavedMemories: (savedMemories) =>
    set((draft) => {
      draft.savedMemories = savedMemories;
    }),

  setPreferredSubsection: (subsection) =>
    set((draft) => {
      draft.preferredSubsection = subsection;
    }),

  addMemory: (memory) =>
    set((draft) => {
      draft.savedMemories.push(memory);
    }),

  removeMemory: (memoryId) =>
    set((draft) => {
      draft.savedMemories = draft.savedMemories.filter((memory) => memory.id !== memoryId);
    }),

  updateMemory: (memory) =>
    set((draft) => {
      draft.savedMemories = draft.savedMemories.map((existing) => (existing.id === memory.id ? memory : existing));
    }),

  addDraftAttachment: (attachment) =>
    set((draft) => {
      draft.draftAttachments.push(attachment);
    }),

  removeDraftAttachment: (attachmentId) =>
    set((draft) => {
      draft.draftAttachments = draft.draftAttachments.filter((attachment) => attachment.id !== attachmentId);
    }),

  clearDraftAttachments: () =>
    set((draft) => {
      draft.draftAttachments = [];
    }),

  setLibraryEditorState: (id, libraryEditorDisplayed) =>
    set((draft) => {
      draft.editingLibraryItemId = id;
      draft.libraryEditorDisplayed = libraryEditorDisplayed;
    }),

  addAskUserQuestion: (question) =>
    set((draft) => {
      if (draft.askUserQuestions[question.id]) return;
      draft.askUserQuestions[question.id] = question;
    }),

  updateAskUserDraft: (questionId, draft, replace) =>
    set((state) => {
      const previous = replace ? {} : (state.askUserDrafts[questionId] ?? {});
      state.askUserDrafts[questionId] = { ...previous, ...draft };
    }),

  resolveAskUserQuestion: (questionId, answer) =>
    set((draft) => {
      const existing = draft.askUserQuestions[questionId];
      if (!existing) return;
      existing.status = answer.kind === 'skipped' ? 'skipped' : 'answered';
      existing.answer = answer;
      delete draft.askUserDrafts[questionId];
    }),

  cancelAskUserQuestionsForChat: (chatId) =>
    set((draft) => {
      // Every draft belonging to this chat is stale once the stream is aborted, even for questions that were
      // already answered/skipped; only pending (unanswered) question records themselves are removed.
      for (const [id, question] of Object.entries(draft.askUserQuestions)) {
        if (question.chatId !== chatId) continue;
        delete draft.askUserDrafts[id];
        if (question.status === 'pending') {
          delete draft.askUserQuestions[id];
        }
      }
    }),

  setAskUserPanelCollapsed: (collapsed) =>
    set((draft) => {
      draft.askUserPanelCollapsed = collapsed;
    }),

  reset: resetState,
});

const STORE_NAME = 'AiAssistant';

const [AiAssistantStoreProvider, useAiAssistantStoreInstance, useAiAssistantSelector] = createStore(
  STORE_NAME,
  initialState,
  aiAssistantActionsFactory,
);

export { AiAssistantStoreProvider };

export const useAiAssistantDisplayed = () => useAiAssistantSelector((state) => state.displayed);
export const useAiAssistantMinimized = () => useAiAssistantSelector((state) => state.minimized);
export const useAiAssistantMaximized = () => useAiAssistantSelector((state) => state.maximized);
export const useAiAssistantLibraryDisplayed = () => useAiAssistantSelector((state) => state.libraryDisplayed);
export const useAiAssistantLibraryEditorDisplayed = () =>
  useAiAssistantSelector((state) => state.libraryEditorDisplayed);
export const useAiAssistantStartNewChatOnOpen = () => useAiAssistantSelector((state) => state.startNewChatOnOpen);
export const useAiAssistantMessages = () => useAiAssistantSelector((state) => state.messages);
export const useAiAssistantSelectedAgent = () => useAiAssistantSelector((state) => state.selectedAgent);
export const useAiAssistantFontSize = () => useAiAssistantSelector((state) => state.fontSize);
export const useAiAssistantExternalPrompt = () => useAiAssistantSelector((state) => state.externalPrompt);
export const useAiAssistantExternalAgentBuilderPrompt = () =>
  useAiAssistantSelector((state) => state.externalAgentBuilderPrompt);
export const useAiAssistantReasoningStyle = () => useAiAssistantSelector((state) => state.reasoningStyle);
export const useAiAssistantReasoningEffort = () => useAiAssistantSelector((state) => state.reasoningEffort);
export const useAiAssistantRefreshHistory = () => useAiAssistantSelector((state) => state.refreshHistory);
export const useAiAssistantActiveChat = () => useAiAssistantSelector((state) => state.activeChat);
export const useAiAssistantChatToOpen = () => useAiAssistantSelector((state) => state.chatToOpen);
export const useAiAssistantHistorySearchValue = () => useAiAssistantSelector((state) => state.historySearchValue);
export const useAiAssistantSelectedDocument = () => useAiAssistantSelector((state) => state.selectedDocument);
export const useAiAssistantGenerateMemory = () => useAiAssistantSelector((state) => state.generateMemory);
export const useAiAssistantSavedMemories = () => useAiAssistantSelector((state) => state.savedMemories);
export const useAiAssistantPreferredSubsection = () => useAiAssistantSelector((state) => state.preferredSubsection);
export const useAiAssistantDraftAttachments = () => useAiAssistantSelector((state) => state.draftAttachments);
export const useAiAssistantEditingLibraryItemId = () => useAiAssistantSelector((state) => state.editingLibraryItemId);
export const useAiAssistantAskUserQuestions = () => useAiAssistantSelector((state) => state.askUserQuestions);
export const useAiAssistantAskUserDrafts = () => useAiAssistantSelector((state) => state.askUserDrafts);
export const useAiAssistantAskUserPanelCollapsed = () => useAiAssistantSelector((state) => state.askUserPanelCollapsed);

export const useAiAssistantActions = () => useAiAssistantStoreInstance().actions;

/**
 * @deprecated For legacy singleton bridge only. Non-React consumers (`aiAssistant.actions.ts`, and imperative reads
 * in streaming/skill-editor hooks) use the singleton created from this in core.stores.ts. DO NOT use in new code.
 */
export function legacyCreateAiAssistantStore() {
  return createStoreInstance<AiAssistantState, AiAssistantActions>(initialState, aiAssistantActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistAiAssistantStore = () => {
  const register = useRegisterPersistedStore();
  const store = useAiAssistantStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqAiAssistantStore',
      persistenceLevel: 'WORKBENCH',
      store,
      dehydrate: dehydrateAiAssistantState,
    });
  }, [register, store]);
};
